'use client';

import { BodyLink } from '@jouzen/ecom-components/v3/BodyLink';
import { GridItem } from '@jouzen/ecom-components/v3/GridItem';
import { LayoutGrid } from '@jouzen/ecom-components/v3/LayoutGrid';
import {
  Typography,
  type TypographyProps,
} from '@jouzen/ecom-components/v3/Typography';
import { TypographyRhythm } from '@jouzen/ecom-components/v3/TypographyRhythm';
import { useTranslations } from 'next-intl';
import type { ReactNode } from 'react';
import type { MessageKey } from 'types/LocaleMessageKey';

import RichText from '@/app/components/RichText';

const listValues = {
  li: function li(chunks: ReactNode) {
    return <li className="mb-2 ml-6 list-disc pl-2">{chunks}</li>;
  },
};

const linkValues = {
  Link: (chunks: ReactNode) => {
    const href = Array.isArray(chunks)
      ? (chunks[0] as string)
      : (chunks as string);
    return <BodyLink href={href}>{chunks}</BodyLink>;
  },
};

interface HeadingProps extends Omit<TypographyProps, 'children'> {
  readonly tx: MessageKey;
}

const Heading = ({ tx, ...props }: HeadingProps) => {
  const t = useTranslations();

  return (
    <Typography Element="h2" variant="h3" {...props}>
      {t(tx)}
    </Typography>
  );
};

const Content = () => {
  const t = useTranslations();

  return (
    <LayoutGrid>
      <GridItem colEnd={{ sm: 'main' }} colStart={{ sm: 'main' }}>
        <TypographyRhythm className="pb-32">
          <Heading
            Element="h1"
            variant="h2"
            tx="b2b_terms_cs_page_title"
            className="my-16"
          />
          <Typography>
            <RichText messageKey="b2b_terms_cs_intro" />
          </Typography>

          <section>
            <Heading tx="b2b_terms_cs_meetings_title" />
            <Typography>{t('b2b_terms_cs_meetings_subtitle')}</Typography>
            <ul>
              <RichText
                messageKey="b2b_terms_cs_meetings_list"
                tValues={listValues}
              />
            </ul>
          </section>

          <section>
            <Heading tx="b2b_terms_cs_checks_title" />
            <ul>
              <RichText
                messageKey="b2b_terms_cs_meetings_list"
                tValues={listValues}
              />
            </ul>
          </section>

          <section>
            <Heading
              tx="b2b_terms_cs_training_title"
              variant="h6"
              weight="normal"
            />
            <ul>
              <RichText
                messageKey="b2b_terms_cs_training_list"
                tValues={listValues}
              />
            </ul>
          </section>

          <section>
            <Heading tx="b2b_terms_cs_additional_title" />
            <Typography>
              <RichText
                messageKey="b2b_terms_cs_additional_content"
                tValues={linkValues}
              />
            </Typography>
          </section>

          <Typography className="mt-40">
            {t('legal_disclaimer_translations')}
          </Typography>
        </TypographyRhythm>
      </GridItem>
    </LayoutGrid>
  );
};

export default Content;
